import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Icon, IconButton, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaClipboardCheck, FaEdit, FaTrash } from "react-icons/fa";
import type { MinimumRequirementDto } from "../../../../../autogen/bff-api";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { MinimumRequirementResponseTypeBadge } from "./MinimumRequirementResponseTypeBadge";

interface Props {
  items: MinimumRequirementDto[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

// t('No requirements added yet') t('Add the minimum requirements suppliers must meet')
// t('Add requirement') t('Edit requirement') t('Delete requirement')

export const MinimumRequirementList = ({ items, onAdd, onEdit, onDelete }: Props) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <Box
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        bg="smBackgroundSecondary"
        py="12"
        px="6"
        textAlign="center"
      >
        <Icon as={FaClipboardCheck} boxSize="10" color="smMuted" mb="4" />
        <Text fontWeight="bold" mb="1">
          {t("No requirements added yet")}
        </Text>
        <Text color="smMuted" fontSize="sm" mb="6">
          {t("Add the minimum requirements suppliers must meet")}
        </Text>
        <Button colorScheme="teal" leftIcon={<AddIcon />} onClick={onAdd}>
          {t("Add requirement")}
        </Button>
      </Box>
    );
  }

  return (
    <VStack spacing="3" align="stretch">
      {items.map((item, index) => {
        return (
          <Box
            key={item.id ?? `${item.title}-${index}`}
            data-testid="minimum-requirement-row"
            borderWidth="1px"
            borderColor="smBorder"
            borderRadius="xl"
            bg="smBackground"
            p="5"
            role="group"
            _hover={{ borderColor: "smMuted" }}
          >
            <Flex justify="space-between" align="flex-start">
              <Box flex="1" minW="0">
                <Box mb="2">
                  <MinimumRequirementResponseTypeBadge responseType={item.responseType} />
                </Box>
                <Text fontWeight="bold" mb="1">
                  {item.title}
                </Text>
                {item.description && (
                  <Box color="smMuted" fontSize="sm">
                    <TipTap content={item.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
                  </Box>
                )}
              </Box>
              <Flex
                gap="1"
                ml="2"
                opacity={0}
                _groupHover={{ opacity: 1 }}
                _focusWithin={{ opacity: 1 }}
                transition="opacity 0.15s"
              >
                <IconButton
                  aria-label={t("Edit requirement")}
                  icon={<FaEdit />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onEdit(index)}
                />
                <IconButton
                  aria-label={t("Delete requirement")}
                  icon={<FaTrash />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => onDelete(index)}
                />
              </Flex>
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );
};
